import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import {
  ArrowLeft, Clock, MapPin, Users, Calendar, CheckCircle,
  XCircle, ExternalLink, ChevronRight, Zap, Building2, AlertTriangle, BarChart2,
} from "lucide-react";
import { useTheme } from "../context/ThemeContext";
import { NSWMarginalSeatsWidget } from "../components/NSWMarginalSeatsWidget";
import { NSWChecklistWidget } from "../components/NSWChecklistWidget";

// NSW election: Saturday, March 27, 2027 (fixed 4-year term from March 25, 2023)
const ELECTION_DATE = new Date("2027-03-27T08:00:00+11:00");

const NSW_LEADERS = [
  {
    id: "chris-minns",
    name: "Chris Minns",
    role: "Premier of NSW — seeking first re-election",
    party: "Australian Labor Party",
    color: "#CC2529",
    slogan: "Building the NSW We All Deserve",
    inParlSince: 2011,
    electorate: "Kogarah",
    highlights: [
      "Led Labor to power in March 2023, ending 12 years of Liberal government",
      "Aggressive housing reform — TOD upzoning near train stations",
      "Cashless gaming card legislation passed to reduce poker machine harm",
      "Cost-of-living relief — $150 energy rebate per household",
      "New Sydney Metro lines progressing: West Sydney, Beaches Link",
    ],
    criticisms: [
      "Housing supply targets not being met — planning reforms controversial",
      "Cashless gaming card created backlash from clubs industry",
      "Train reliability and bus network still unreliable in outer suburbs",
      "State debt growing; budget deficit persists",
    ],
  },
  {
    id: "mark-speakman",
    name: "Mark Speakman",
    role: "Leader of the Opposition — challenging for Premier",
    party: "Liberal Party of Australia",
    color: "#1B4F9E",
    slogan: "Putting NSW First",
    inParlSince: 2011,
    electorate: "Cronulla",
    highlights: [
      "Former Attorney-General and leading barrister",
      "Opposed cashless gaming card — protecting hospitality industry",
      "Detailed housing affordability plan focusing on stamp duty reform",
      "Infrastructure reliability — commit to fixing transport before new projects",
      "Stronger economic management — reduce state borrowing",
    ],
    criticisms: [
      "Liberals carry legacy of 12 years of government — some seen as 'more of the same'",
      "Opposition to cashless gaming seen as pro-pokies lobby by critics",
      "Policy detail on housing and energy still developing",
      "Coalition with Nationals requires balancing regional vs Sydney interests",
    ],
  },
];

const NSW_ISSUES = [
  {
    id: "housing",
    title: "Housing Affordability",
    icon: "🏠",
    color: "#DC2626",
    description: "Sydney has the second-lowest housing affordability of any major global city. Median house price above $1.4M. Renters face near-record low vacancy rates and rents 40% higher than 2020.",
    labor: { position: "Supply via TOD upzoning", detail: "Minns passed landmark TOD (Transport Oriented Development) laws allowing 6-storey apartments within 400m of train stations. Targeting 314,000 new homes by 2029. Met with NIMBY resistance but seen as bold reform." },
    liberal: { position: "Stamp duty abolition + incentives", detail: "Speakman would abolish stamp duty for first home buyers on homes up to $1.5M. Priority to reduce developer taxes that add cost to new homes. Fewer planning mandates, more market-led supply." },
    verdict: "Housing is the #1 issue for NSW voters — Labor has legislated supply reform, Liberals prefer tax cuts over mandates.",
  },
  {
    id: "cost-of-living",
    title: "Cost of Living",
    icon: "💰",
    color: "#F59E0B",
    description: "NSW households are under severe financial pressure — electricity bills, mortgage rates, insurance costs and grocery prices all at record highs since 2022.",
    labor: { position: "Targeted rebates + wage growth", detail: "$150 energy bill relief delivered. $3.5B cost-of-living package including school meals, cheaper rego and toll relief. Record public sector wage rises for teachers, nurses and police." },
    liberal: { position: "Tax cuts + competition reform", detail: "Abolish stamp duty for all buyers (replace with property tax). Cut payroll tax for small business. Competition review of supermarkets and insurance. Long-term economic management over short-term rebates." },
    verdict: "Both parties are offering cost-of-living relief — the difference is Labor targeting rebates vs Liberals targeting structural tax reform.",
  },
  {
    id: "transport",
    title: "Transport & Infrastructure",
    icon: "🚆",
    color: "#2563EB",
    description: "Sydney's bus network is plagued by driver shortages. Train reliability hit record lows in 2022-23. Western Sydney Airport (Nancy-Bird Walton) opens in 2026 — unlocking major growth potential.",
    labor: { position: "Fix what's broken + new metro", detail: "Hired 2,000+ extra bus drivers. Sydney Metro West progressing (CBD to Parramatta by 2030). Western Sydney Metro funded. Nancy-Bird Walton Airport road and rail planning underway." },
    liberal: { position: "Reliability before new projects", detail: "Speakman argues for fixing the existing network first. Would pause further metro planning until Sydney Metro West is on track. Faster planning approvals for road duplications in outer suburbs." },
    verdict: "Western Sydney Airport is the biggest infrastructure wildcard — whoever controls the government will shape the transport links around it.",
  },
  {
    id: "health",
    title: "Health & Hospitals",
    icon: "🏥",
    color: "#7C3AED",
    description: "NSW ambulance ramping and hospital wait times are among the worst in Australia. Nurses' industrial action in 2022-23 highlighted chronic understaffing.",
    labor: { position: "Record investment + wage rises", detail: "7,000+ new nurses and health workers hired. 15% pay rise for nurses delivered after 2022 industrial dispute. $5.4B health building program. New hospitals at Liverpool and Bankstown." },
    liberal: { position: "Targets + accountability", detail: "Mandatory ambulance response time targets. Hospital performance dashboards published monthly. Would freeze non-frontline public service hiring and redirect to clinical roles." },
    verdict: "Health is Minns' biggest test — significant investment has been made but ramping and waiting times haven't fully recovered yet.",
  },
  {
    id: "energy",
    title: "Energy & Climate",
    icon: "⚡",
    color: "#10B981",
    description: "NSW must replace three major coal power stations closing by 2030. The Eraring extension (Australia's biggest coal plant) was controversially approved. The Energy Transition is the defining infrastructure challenge.",
    labor: { position: "Accelerate renewables + Eraring safety net", detail: "NSW Electricity Investment Roadmap: $32B in new renewable energy zones (New England, Central-West, Hunter Liddell). Approved Eraring extension as short-term reliability safety net. 12GW offshore wind target." },
    liberal: { position: "Reliability first + gas transition", detail: "Speakman supports renewables but argues Eraring should run longer to prevent blackouts. Open to extending coal operations until storage and transmission is built. Prioritise energy reliability over emissions targets." },
    verdict: "NSW energy is the most technically complex election issue — voters must judge whether Labor's transition pace or Liberal's caution is better for energy security.",
  },
];

const NSW_PARTIES = [
  { name: "ALP", full: "Australian Labor Party (NSW)", color: "#CC2529", seats2023: 45, leader: "Chris Minns", likelihood: "Favourite" },
  { name: "Liberal", full: "Liberal Party of Australia (NSW)", color: "#1B4F9E", seats2023: 24, leader: "Mark Speakman", likelihood: "Challenger" },
  { name: "National", full: "The Nationals (NSW)", color: "#006633", seats2023: 13, leader: "Dugald Saunders", likelihood: "Regional power" },
  { name: "Greens", full: "Australian Greens (NSW)", color: "#10B981", seats2023: 4, leader: "Jenny Leong", likelihood: "Inner-city seats" },
  { name: "Ind/Teal", full: "Independents & Teal candidates", color: "#0D9488", seats2023: 7, leader: "Various", likelihood: "Key swing seats" },
];

const NSW_POLLS = [
  { pollster: "Redbridge", date: "Feb 2026", alp: 36, lib: 30, grn: 13, oth: 21, two_pp_alp: 54, two_pp_lib: 46 },
  { pollster: "Essential", date: "Jan 2026", alp: 35, lib: 31, grn: 12, oth: 22, two_pp_alp: 53, two_pp_lib: 47 },
  { pollster: "Newspoll NSW", date: "Nov 2025", alp: 37, lib: 29, grn: 13, oth: 21, two_pp_alp: 55, two_pp_lib: 45 },
  { pollster: "Roy Morgan", date: "Oct 2025", alp: 38, lib: 28, grn: 14, oth: 20, two_pp_alp: 56, two_pp_lib: 44 },
];

const NSW_TREND = [
  { month: "Oct '25", ALP: 38, Coalition: 28, Greens: 14 },
  { month: "Nov '25", ALP: 37, Coalition: 29, Greens: 13 },
  { month: "Jan '26", ALP: 35, Coalition: 31, Greens: 12 },
  { month: "Feb '26", ALP: 36, Coalition: 30, Greens: 13 },
];

const KEY_DATES = [
  { label: "Last day to enrol", date: "2027-03-08", note: "~19 days before election" },
  { label: "Pre-poll opens", date: "2027-03-15", note: "12 days before election" },
  { label: "Pre-poll closes", date: "2027-03-26", note: "Friday before election" },
  { label: "ELECTION DAY", date: "2027-03-27", note: "Polls open 8am – 6pm AEDT", highlight: true },
];

import { useCountdown, CountdownBox } from "../components/ElectionCountdown";


export default function NSW2027Page() {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const countdown = useCountdown(ELECTION_DATE);

  // Scroll to top on mount (navigation doesn't auto-scroll in React Router)
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const bg = isDark ? "#080E1A" : "#F8FAFC";
  const surface = isDark ? "#1E293B" : "#FFFFFF";
  const text = isDark ? "#F8FAFC" : "#0F172A";
  const textSec = isDark ? "#94A3B8" : "#475569";
  const borderC = isDark ? "rgba(51,65,85,0.55)" : "rgba(226,232,240,0.85)";
  const shadowMd = isDark ? "0 4px 20px -2px rgba(0,0,0,0.35)" : "0 4px 16px -2px rgba(15,23,42,0.07)";

  return (
    <div className="min-h-screen" style={{ backgroundColor: bg }} data-testid="nsw2027-page">

      {/* ── Hero ── */}
      <div
        className="relative overflow-hidden"
        style={{
          background: "linear-gradient(160deg, #0F172A 0%, #0D1B35 40%, #0F172A 100%)",
          borderBottom: "1px solid rgba(51,65,85,0.35)",
        }}
      >
        {/* Ambient glow */}
        <div style={{
          position: "absolute", top: 0, right: "20%",
          width: 500, height: 400,
          background: "radial-gradient(ellipse at center, rgba(27,79,158,0.12) 0%, transparent 70%)",
          filter: "blur(50px)", pointerEvents: "none",
        }} />
        <div style={{ height: 3, background: "linear-gradient(90deg, #1B4F9E, #3B82F688, transparent)" }} />

        <div className="max-w-5xl mx-auto px-4 py-12 relative">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-1.5 text-sm font-medium mb-8 transition-all hover:opacity-70"
            style={{ color: "#FACC15", background: "none", border: "none", cursor: "pointer" }}
            data-testid="back-home-btn"
          >
            <ArrowLeft style={{ width: 15, height: 15 }} /> Home
          </button>

          <div className="flex items-center gap-2 mb-5 flex-wrap">
            <span
              className="text-xs font-semibold px-3 py-1.5 rounded-full"
              style={{ background: "rgba(27,79,158,0.15)", color: "#93C5FD", border: "1px solid rgba(27,79,158,0.25)" }}
            >
              NSW 2027
            </span>
            <span
              className="text-xs font-semibold px-3 py-1.5 rounded-full"
              style={{ background: "rgba(234,179,8,0.10)", color: "#FACC15", border: "1px solid rgba(234,179,8,0.20)" }}
            >
              ELECTION GUIDE
            </span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white tracking-tighter mb-3" style={{ fontFamily: "Outfit, sans-serif" }}>
            New South Wales<br />State Election 2027
          </h1>
          <p className="text-base mb-8" style={{ color: "#64748B" }}>
            Saturday, March 27, 2027 · Legislative Assembly + Legislative Council · 93 lower house seats
          </p>

          {!countdown.over ? (
            <div>
              <p className="text-xs font-semibold mb-4 tracking-widest uppercase" style={{ color: "#93C5FD" }}>
                Time until polls open
              </p>
              <div className="flex items-end gap-2 sm:gap-4 flex-wrap">
                <CountdownBox value={countdown.days} label="Days" />
                <span className="text-2xl sm:text-3xl font-black text-white mb-7 opacity-30">:</span>
                <CountdownBox value={countdown.hours} label="Hours" />
                <span className="text-2xl sm:text-3xl font-black text-white mb-7 opacity-30">:</span>
                <CountdownBox value={countdown.minutes} label="Mins" />
                <span className="text-2xl sm:text-3xl font-black text-white mb-7 opacity-30">:</span>
                <CountdownBox value={countdown.seconds} label="Secs" />
              </div>
            </div>
          ) : (
            <div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl"
              style={{ background: "rgba(27,79,158,0.15)", border: "1px solid rgba(27,79,158,0.25)" }}
            >
              <Zap style={{ width: 15, height: 15, color: "#93C5FD" }} />
              <span className="font-semibold" style={{ color: "#93C5FD" }}>NSW has voted — explore results</span>
            </div>
          )}

          {/* Key dates */}
          <div className="flex flex-wrap gap-2.5 mt-10">
            {KEY_DATES.map((d) => (
              <div
                key={d.label}
                className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs"
                style={{
                  background: d.highlight ? "rgba(27,79,158,0.15)" : "rgba(255,255,255,0.05)",
                  border: `1px solid ${d.highlight ? "rgba(27,79,158,0.28)" : "rgba(51,65,85,0.4)"}`,
                  color: d.highlight ? "#93C5FD" : "#64748B",
                }}
              >
                <Calendar style={{ width: 12, height: 12, flexShrink: 0 }} />
                <span className="font-semibold">{d.label}:</span>
                <span>{d.date}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-12 space-y-16">

        {/* ── Head to Head ── */}
        <section data-testid="leaders-section">
          <div className="flex items-center gap-2.5 mb-6">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "rgba(234,179,8,0.10)", border: "1px solid rgba(234,179,8,0.20)" }}>
              <Users style={{ width: 18, height: 18, color: "#EAB308" }} />
            </div>
            <h2 className="text-2xl font-bold tracking-tight" style={{ color: text, fontFamily: "Outfit, sans-serif" }}>
              Head-to-Head: Who's Running for Premier?
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {NSW_LEADERS.map((leader) => (
              <div
                key={leader.id}
                className="overflow-hidden transition-all duration-200 hover:-translate-y-1"
                style={{ background: surface, border: `1px solid ${borderC}`, borderRadius: 18, boxShadow: shadowMd }}
                data-testid={`leader-card-${leader.id}`}
              >
                <div style={{ height: 3, background: `linear-gradient(90deg, ${leader.color}, ${leader.color}66)` }} />
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-bold mb-1.5 tracking-tight" style={{ color: text, fontFamily: "Outfit, sans-serif" }}>{leader.name}</h3>
                      <span className="text-xs font-semibold px-2.5 py-1 rounded-full text-white" style={{ background: leader.color }}>
                        {leader.party}
                      </span>
                      <p className="text-xs mt-2" style={{ color: textSec }}>{leader.role}</p>
                    </div>
                  </div>
                  <p className="text-sm font-semibold mb-1 italic" style={{ color: leader.color }}>"{leader.slogan}"</p>
                  <p className="text-xs mb-4" style={{ color: textSec }}>
                    Electorate: {leader.electorate} · In parliament since {leader.inParlSince}
                  </p>
                  <div className="mb-3">
                    <p className="text-xs font-semibold mb-2" style={{ color: "#10B981" }}>Key achievements / promises:</p>
                    {leader.highlights.map((h, i) => (
                      <div key={i} className="flex items-start gap-1.5 mb-1">
                        <CheckCircle style={{ width: 13, height: 13, flexShrink: 0, marginTop: 2, color: "#10B981" }} />
                        <span className="text-xs" style={{ color: text }}>{h}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mb-4">
                    <p className="text-xs font-semibold mb-2" style={{ color: "#F43F5E" }}>Criticisms:</p>
                    {leader.criticisms.map((c, i) => (
                      <div key={i} className="flex items-start gap-1.5 mb-1">
                        <XCircle style={{ width: 13, height: 13, flexShrink: 0, marginTop: 2, color: "#F43F5E" }} />
                        <span className="text-xs" style={{ color: text }}>{c}</span>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={() => navigate(`/politicians/${leader.id}`)}
                    className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-xs font-semibold transition-all hover:-translate-y-0.5 active:scale-[0.98]"
                    style={{ background: leader.color, color: "#fff", boxShadow: `0 4px 14px ${leader.color}33`, border: "none", cursor: "pointer" }}
                    data-testid={`view-profile-${leader.id}`}
                  >
                    View Full Profile + Promise Tracker <ChevronRight style={{ width: 14, height: 14 }} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── Key Issues ── */}
        <section data-testid="issues-section">
          <div className="flex items-center gap-2.5 mb-6">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "rgba(234,179,8,0.10)", border: "1px solid rgba(234,179,8,0.20)" }}>
              <AlertTriangle style={{ width: 18, height: 18, color: "#EAB308" }} />
            </div>
            <h2 className="text-2xl font-bold tracking-tight" style={{ color: text, fontFamily: "Outfit, sans-serif" }}>
              5 Key Issues Deciding the NSW Election
            </h2>
          </div>
          <div className="space-y-4">
            {NSW_ISSUES.map((issue) => (
              <div
                key={issue.id}
                className="overflow-hidden transition-all duration-200"
                style={{ background: surface, border: `1px solid ${borderC}`, borderRadius: 16, boxShadow: shadowMd }}
                data-testid={`issue-${issue.id}`}
              >
                <div style={{ height: 2.5, background: `linear-gradient(90deg, ${issue.color}, ${issue.color}55)` }} />
                <div className="p-5">
                  <div className="flex items-start gap-3 mb-4">
                    <span className="text-2xl">{issue.icon}</span>
                    <div className="flex-1">
                      <h3 className="text-base font-bold mb-1 tracking-tight" style={{ color: text, fontFamily: "Outfit, sans-serif" }}>{issue.title}</h3>
                      <p className="text-sm" style={{ color: textSec }}>{issue.description}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                    <div
                      className="pl-3 py-2 rounded-r-xl"
                      style={{ borderLeft: "3px solid #CC2529", background: isDark ? "rgba(204,37,41,0.04)" : "rgba(204,37,41,0.02)" }}
                    >
                      <p className="text-xs font-semibold mb-1" style={{ color: "#CC2529" }}>Labor — {issue.labor.position}</p>
                      <p className="text-xs" style={{ color: textSec }}>{issue.labor.detail}</p>
                    </div>
                    <div
                      className="pl-3 py-2 rounded-r-xl"
                      style={{ borderLeft: "3px solid #1B4F9E", background: isDark ? "rgba(27,79,158,0.04)" : "rgba(27,79,158,0.02)" }}
                    >
                      <p className="text-xs font-semibold mb-1" style={{ color: "#1B4F9E" }}>Liberal — {issue.liberal.position}</p>
                      <p className="text-xs" style={{ color: textSec }}>{issue.liberal.detail}</p>
                    </div>
                  </div>
                  <div
                    className="flex items-start gap-2 mt-3 p-3 rounded-xl"
                    style={{ background: isDark ? "rgba(234,179,8,0.06)" : "rgba(234,179,8,0.05)", border: "1px solid rgba(234,179,8,0.14)" }}
                  >
                    <Zap style={{ width: 13, height: 13, flexShrink: 0, marginTop: 1, color: "#EAB308" }} />
                    <p className="text-xs font-medium" style={{ color: textSec }}>{issue.verdict}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── Checklist ── */}
        <NSWChecklistWidget />

        {/* ── Parties ── */}
        <section data-testid="parties-section">
          <div className="flex items-center gap-2.5 mb-6">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "rgba(234,179,8,0.10)", border: "1px solid rgba(234,179,8,0.20)" }}>
              <Building2 style={{ width: 18, height: 18, color: "#EAB308" }} />
            </div>
            <h2 className="text-2xl font-bold tracking-tight" style={{ color: text, fontFamily: "Outfit, sans-serif" }}>
              Parties Running in NSW 2027
            </h2>
          </div>
          <div className="overflow-hidden" style={{ border: `1px solid ${borderC}`, borderRadius: 16, boxShadow: shadowMd }}>
            <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
              <table className="w-full text-sm" style={{ minWidth: 520 }}>
                <thead>
                  <tr style={{ background: isDark ? "#0F172A" : "#F8FAFC", borderBottom: `1px solid ${borderC}` }}>
                    {["Party", "Full Name", "Leader", "2023 Lower House", "2027 Outlook"].map((h) => (
                      <th key={h} className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider" style={{ color: textSec }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {NSW_PARTIES.map((p, i) => (
                    <tr
                      key={p.name}
                      style={{ background: i % 2 === 0 ? surface : (isDark ? "rgba(15,23,42,0.4)" : "#F8FAFC") }}
                      data-testid={`party-row-${p.name.toLowerCase().replace(/[\/ ]/g, "-")}`}
                    >
                      <td className="py-3 px-4 font-bold border-b text-sm" style={{ color: p.color, borderColor: borderC }}>{p.name}</td>
                      <td className="py-3 px-4 border-b text-xs" style={{ color: textSec, borderColor: borderC }}>{p.full}</td>
                      <td className="py-3 px-4 border-b text-xs" style={{ color: text, borderColor: borderC }}>{p.leader}</td>
                      <td className="py-3 px-4 border-b text-center font-bold" style={{ color: text, borderColor: borderC }}>{p.seats2023}</td>
                      <td className="py-3 px-4 border-b" style={{ borderColor: borderC }}>
                        <span
                          className="text-xs font-semibold px-2.5 py-1 rounded-full"
                          style={{ background: `${p.color}12`, color: p.color, border: `1px solid ${p.color}22` }}
                        >
                          {p.likelihood}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <p className="text-xs mt-2" style={{ color: textSec }}>* 2023 results: ALP 45, Liberal 24, Nationals 13, Greens 4, Ind/Other 7. 93 total lower house seats.</p>
        </section>

        {/* ── Opinion Polls ── */}
        <section data-testid="nsw-polls-section">
          <div className="flex items-center gap-2.5 mb-4">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "rgba(234,179,8,0.10)", border: "1px solid rgba(234,179,8,0.20)" }}>
              <BarChart2 style={{ width: 18, height: 18, color: "#EAB308" }} />
            </div>
            <div>
              <h2 className="text-2xl font-bold tracking-tight" style={{ color: text, fontFamily: "Outfit, sans-serif" }}>NSW 2027 Opinion Polls</h2>
              <p className="text-sm" style={{ color: textSec }}>Primary vote estimates — Labor leads early polling, race to tighten</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
            {[{ party: "ALP — Chris Minns", color: "#CC2529", tpp: 54 }, { party: "Liberal/Nat — Mark Speakman", color: "#1B4F9E", tpp: 46 }].map((p) => (
              <div
                key={p.party}
                className="overflow-hidden"
                style={{ background: surface, border: `1px solid ${borderC}`, borderRadius: 16, boxShadow: shadowMd, padding: "16px 18px" }}
              >
                <div style={{ height: 2, background: `linear-gradient(90deg, ${p.color}, ${p.color}44)`, marginBottom: 12 }} />
                <p className="text-xs font-semibold mb-1" style={{ color: p.color }}>{p.party}</p>
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="text-3xl font-black" style={{ color: p.color, fontFamily: "Outfit, sans-serif" }}>{p.tpp}%</span>
                  <span className="text-xs" style={{ color: textSec }}>two-party-preferred</span>
                </div>
                <div className="w-full h-2 rounded-full overflow-hidden" style={{ background: isDark ? "rgba(51,65,85,0.4)" : "#E2E8F0" }}>
                  <div className="h-full rounded-full" style={{ background: `linear-gradient(90deg, ${p.color}, ${p.color}cc)`, width: `${p.tpp}%` }} />
                </div>
              </div>
            ))}
          </div>

          <div
            style={{ background: surface, border: `1px solid ${borderC}`, borderRadius: 16, boxShadow: shadowMd, padding: "20px" }}
            data-testid="nsw-poll-trend-chart"
          >
            <p className="text-xs font-semibold mb-4 uppercase tracking-wider" style={{ color: textSec }}>Primary Vote Trend — Oct 2025 to Feb 2026</p>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={NSW_TREND} barGap={4}>
                <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "rgba(51,65,85,0.3)" : "#E2E8F0"} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: textSec }} axisLine={false} tickLine={false} />
                <YAxis domain={[0, 50]} tick={{ fontSize: 11, fill: textSec }} tickFormatter={(v) => `${v}%`} axisLine={false} tickLine={false} />
                <Tooltip
                  formatter={(v) => `${v}%`}
                  contentStyle={{
                    background: isDark ? "rgba(30,41,59,0.95)" : "#fff",
                    border: `1px solid ${borderC}`,
                    borderRadius: 12,
                    backdropFilter: "blur(8px)",
                    color: text,
                    boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
                  }}
                />
                <Bar dataKey="ALP" fill="#CC2529" radius={[4, 4, 0, 0]} name="Labor" />
                <Bar dataKey="Coalition" fill="#1B4F9E" radius={[4, 4, 0, 0]} name="Liberal/Nat" />
                <Bar dataKey="Greens" fill="#10B981" radius={[4, 4, 0, 0]} name="Greens" />
              </BarChart>
            </ResponsiveContainer>
            <div className="flex items-center gap-5 mt-3">
              {[{ color: "#CC2529", label: "Labor" }, { color: "#1B4F9E", label: "Liberal/Nat" }, { color: "#10B981", label: "Greens" }].map((l) => (
                <div key={l.label} className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-sm" style={{ background: l.color }} />
                  <span className="text-xs font-medium" style={{ color: textSec }}>{l.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div
            className="mt-4 overflow-hidden"
            style={{ border: `1px solid ${borderC}`, borderRadius: 16 }}
            data-testid="nsw-polls-table"
          >
            <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
              <table className="w-full text-sm" style={{ minWidth: 560 }}>
                <thead>
                  <tr style={{ background: isDark ? "#0F172A" : "#F8FAFC", borderBottom: `1px solid ${borderC}` }}>
                    {["Pollster", "Date", "ALP", "Lib/Nat", "Greens", "Others", "ALP 2PP", "Lib 2PP"].map((h) => (
                      <th key={h} className="text-left py-3 px-3 text-xs font-semibold uppercase tracking-wider" style={{ color: textSec }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {NSW_POLLS.map((p, i) => (
                    <tr key={p.pollster} style={{ background: i % 2 === 0 ? surface : (isDark ? "rgba(15,23,42,0.4)" : "#F8FAFC") }}>
                      <td className="py-2.5 px-3 font-semibold border-b text-xs" style={{ color: text, borderColor: borderC }}>{p.pollster}</td>
                      <td className="py-2.5 px-3 border-b text-xs" style={{ color: textSec, borderColor: borderC }}>{p.date}</td>
                      <td className="py-2.5 px-3 border-b font-bold text-xs" style={{ color: "#CC2529", borderColor: borderC }}>{p.alp}%</td>
                      <td className="py-2.5 px-3 border-b font-bold text-xs" style={{ color: "#1B4F9E", borderColor: borderC }}>{p.lib}%</td>
                      <td className="py-2.5 px-3 border-b font-bold text-xs" style={{ color: "#10B981", borderColor: borderC }}>{p.grn}%</td>
                      <td className="py-2.5 px-3 border-b text-xs" style={{ color: textSec, borderColor: borderC }}>{p.oth}%</td>
                      <td className="py-2.5 px-3 border-b font-bold text-xs" style={{ color: "#CC2529", borderColor: borderC }}>{p.two_pp_alp}%</td>
                      <td className="py-2.5 px-3 border-b font-bold text-xs" style={{ color: "#1B4F9E", borderColor: borderC }}>{p.two_pp_lib}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <p className="text-xs mt-2" style={{ color: textSec }}>* NSW state polling estimates. Source: Redbridge, Essential, Newspoll NSW, Roy Morgan.</p>
        </section>

        {/* ── Marginal Seats ── */}
        <NSWMarginalSeatsWidget />

        {/* ── How to Vote ── */}
        <section data-testid="how-to-vote-section">
          <div className="flex items-center gap-2.5 mb-6">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "rgba(16,185,129,0.10)", border: "1px solid rgba(16,185,129,0.18)" }}>
              <MapPin style={{ width: 18, height: 18, color: "#10B981" }} />
            </div>
            <h2 className="text-2xl font-bold tracking-tight" style={{ color: text, fontFamily: "Outfit, sans-serif" }}>
              How to Vote in NSW 2027
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              {
                title: "Check Your Enrolment",
                icon: CheckCircle,
                color: "#10B981",
                body: "Make sure you're enrolled by March 8, 2027. NSW rolls close ~19 days before election. Check at elections.nsw.gov.au",
                link: "https://www.elections.nsw.gov.au",
                linkText: "Check enrolment →",
              },
              {
                title: "Find Your Polling Place",
                icon: MapPin,
                color: "#1B4F9E",
                body: "NSW uses optional preferential voting for the Legislative Assembly — you can number as few or as many boxes as you like.",
                link: "https://www.elections.nsw.gov.au",
                linkText: "Find a polling place →",
              },
              {
                title: "Voting System",
                icon: Clock,
                color: "#3B82F6",
                body: "Lower house: at least 1 box (but numbering more is recommended — exhaust risks).\nUpper house: at least 15 candidates below the line or 1 group above the line.",
                link: "https://www.elections.nsw.gov.au/voting/how-to-vote",
                linkText: "Learn how to vote →",
              },
            ].map((card) => {
              const Icon = card.icon;
              return (
                <div
                  key={card.title}
                  className="overflow-hidden transition-all duration-200 hover:-translate-y-1"
                  style={{ background: surface, border: `1px solid ${borderC}`, borderRadius: 16, boxShadow: shadowMd }}
                >
                  <div style={{ height: 2.5, background: `linear-gradient(90deg, ${card.color}, ${card.color}44)` }} />
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <div
                        className="w-8 h-8 rounded-lg flex items-center justify-center"
                        style={{ background: `${card.color}10`, border: `1px solid ${card.color}20` }}
                      >
                        <Icon style={{ width: 16, height: 16, color: card.color }} />
                      </div>
                      <h3 className="font-semibold text-sm tracking-tight" style={{ color: text }}>{card.title}</h3>
                    </div>
                    <p className="text-xs mb-3 leading-relaxed whitespace-pre-line" style={{ color: textSec }}>{card.body}</p>
                    <a
                      href={card.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs font-semibold flex items-center gap-1 transition-all"
                      style={{ color: card.color }}
                    >
                      {card.linkText} <ExternalLink style={{ width: 11, height: 11 }} />
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* ── Quick Tools ── */}
        <section data-testid="quick-tools-section">
          <h2 className="text-2xl font-bold mb-5 tracking-tight" style={{ color: text, fontFamily: "Outfit, sans-serif" }}>
            Useful Tools for NSW Voters
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { label: "NSW Politicians", desc: "Minns, Speakman + full team", path: "/politicians?level=state", color: "#CC2529" },
              { label: "Promise Tracker", desc: "Track what they promised NSW", path: "/promises", color: "#1B4F9E" },
              { label: "Promise Leaderboard", desc: "Who delivers on promises?", path: "/leaderboard", color: "#D97706" },
              { label: "Party Match Quiz", desc: "Which NSW party matches you?", path: "/quiz", color: "#10B981" },
            ].map((t) => (
              <button
                key={t.label}
                onClick={() => navigate(t.path)}
                className="text-left transition-all duration-200 hover:-translate-y-0.5 active:scale-[0.98]"
                style={{
                  background: surface,
                  border: `1px solid ${borderC}`,
                  borderRadius: 14,
                  boxShadow: shadowMd,
                  padding: "16px",
                  cursor: "pointer",
                }}
                data-testid={`tool-${t.label.toLowerCase().replace(/ /g, "-")}`}
              >
                <div
                  className="w-1 h-8 rounded-full mb-3"
                  style={{ background: `linear-gradient(180deg, ${t.color}, ${t.color}55)` }}
                />
                <p className="font-semibold text-sm mb-1 tracking-tight" style={{ color: t.color, fontFamily: "Outfit, sans-serif" }}>{t.label}</p>
                <p className="text-xs" style={{ color: textSec }}>{t.desc}</p>
              </button>
            ))}
          </div>
        </section>

        {/* ── Official NSWEC ── */}
        <div
          className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3"
          style={{
            background: isDark ? "rgba(30,41,59,0.6)" : "rgba(27,79,158,0.05)",
            border: "1px solid rgba(27,79,158,0.18)",
            borderRadius: 16,
            padding: "20px 24px",
          }}
          data-testid="official-nswec-section"
        >
          <div>
            <p className="font-semibold text-sm mb-1" style={{ color: text }}>Official NSW Electoral Authority</p>
            <p className="text-xs" style={{ color: textSec }}>Electoral information, candidate lists, and polling place finder at the NSW Electoral Commission.</p>
          </div>
          <a
            href="https://www.elections.nsw.gov.au"
            target="_blank"
            rel="noopener noreferrer"
            className="flex-shrink-0 flex items-center gap-1.5 text-xs font-semibold px-4 py-2.5 rounded-xl transition-all hover:-translate-y-0.5"
            style={{
              background: "linear-gradient(135deg, #1B4F9E, #3B82F6)",
              color: "#fff",
              boxShadow: "0 4px 14px rgba(27,79,158,0.30)",
              textDecoration: "none",
            }}
            data-testid="official-nswec-link"
          >
            elections.nsw.gov.au <ExternalLink style={{ width: 12, height: 12 }} />
          </a>
        </div>

      </div>
    </div>
  );
}
